import {client} from '@core/GraphQLProvider';
import {createQueueManager} from '@core/GraphQLProvider/util';
import {GetCart, AddLineItems, UpdateLineItems, DeleteLineItems} from './query.graphql';
import dataLayer from '../../upi/data-layer';
import {
    Cart,
    cartAdditionsStrategy,
    cartDeletionsStrategy,
    cartUpdatesStrategy,
    mergeCart,
    toCartEntity,
    toLineItemEntity,
    toLineItemInput,
    toLineItemsPayload
} from '@modules/cart/Cart';

const ADD_LINE_ITEMS = 'addLineItems';
const UPDATE_LINE_ITEMS = 'updateLineItems';
const DELETE_LINE_ITEMS = 'deleteLineItems';
const mutationsQueue = createQueueManager( [ADD_LINE_ITEMS, UPDATE_LINE_ITEMS, DELETE_LINE_ITEMS] );
const eventMap = {
    [ADD_LINE_ITEMS]: 'lineItemsAdded',
    [UPDATE_LINE_ITEMS]: 'lineItemsUpdated',
    [DELETE_LINE_ITEMS]: 'lineItemsDeleted'
};

const getCart = async () => {
    const resp = await client.query( {
        query: GetCart
    } );

    return resp.data.cart;
};

const getCartFromCache = () => {
    const {cart} = client.cache.readQuery( {query: GetCart} );

    // ensure we have a cart in the local cache
    if ( !cart ) {
        return seedEmptyCart();
    }

    return cart;
};

const seedEmptyCart = () => {
    const data = {cart: toCartEntity()};
    client.cache.writeQuery( {
        query: GetCart,
        data
    } );

    return data;
};

const syncChangedLineItems = ( mutationName, query ) => ( proxy, {data} ) => {
    const state = proxy.readQuery( {query} );
    // console.log( 'Lines from mutation result:', data[mutationName].cart.lineItems );
    const dataUpdated = {
        cart: {
            ...state.cart,
            lineItems: data[mutationName].cart.lineItems
        }
    };

    proxy.writeQuery( {query, data: dataUpdated} );
};

const trackCartChange = ( changeType, lineItems ) => {

    const productData = lineItems.map( line => {
        return {
            ...line.product,
            quantity: line.quantity
        };
    } );

    switch ( changeType ) {
        case ADD_LINE_ITEMS:
            dataLayer.cart.sendItemsAdded( productData );
            return;
        case DELETE_LINE_ITEMS:
            return dataLayer.cart.sendItemsRemoved( productData );
        default:
            console.warn( `Ignoring unknown tracking type "${changeType}"` );
    }
};

const createLineItemMutation = ( mutation, selection, mergeStrategy ) =>
    async ( lineItems ) => {

        if ( !Array.isArray( lineItems ) ) {
            lineItems = [lineItems];
        }

        // consecutive mutations need to be tracked in a queue to build accurate optimisticResponse object
        const queuedLineItemMutations = mutationsQueue.add(
            selection,
            lineItems.map( toLineItemEntity )
        );

        const cart = getCartFromCache();
        const optimisticCart = mergeCart( mergeStrategy )( cart, queuedLineItemMutations );
        console.log( '[optimisticResponse]', toLineItemsPayload( selection, optimisticCart, queuedLineItemMutations ) );

        try {
            setTimeout( () => {
                Cart.trigger( selection, lineItems );
            }, 10 );

            const resp = await client.mutate( {
                mutation,
                variables: toLineItemInput( lineItems ),
                optimisticResponse: toLineItemsPayload( selection, optimisticCart, queuedLineItemMutations ),
                update: syncChangedLineItems( selection, GetCart )
            } );

            trackCartChange( selection, lineItems );

            // trigger cart change events: e.g. one of ["lineItemsAdded", "lineItemsUpdated", "lineItemsDeleted"]
            setTimeout( () => {
                Cart.trigger( eventMap[selection], lineItems );
                Cart.trigger( 'cartChange', lineItems );
            }, 10 );

            // console.log( `[${selection}] data.cart`, resp.data[selection].cart );
            mutationsQueue.remove( selection, lineItems );
            console.log( 'updated state', resp.data[selection].cart );
            return resp.data[selection].cart;
        }
        catch ( err ) {
            mutationsQueue.remove( selection, lineItems );
        }
    };

export default {
    getCart,
    getCartFromCache,
    addLineItems: createLineItemMutation( AddLineItems, ADD_LINE_ITEMS, cartAdditionsStrategy ),
    updateLineItems: createLineItemMutation( UpdateLineItems, UPDATE_LINE_ITEMS, cartUpdatesStrategy ),
    deleteLineItems: createLineItemMutation( DeleteLineItems, DELETE_LINE_ITEMS, cartDeletionsStrategy )
};











